import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { portfolio } from "../data/portfolio";
import { useTheme } from "../hooks/useTheme";
import { GitHubIcon, GmailIcon, LinkedInIcon, MediumIcon, SubstackIcon, XIcon } from "../components/BrandIcons";

export function Landing() {
  const [theme, toggleTheme] = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add("landing-body");
    return () => document.body.classList.remove("landing-body");
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        const tag = (document.activeElement as HTMLElement | null)?.tagName ?? "";
        if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "BUTTON") {
          navigate("/portfolio");
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const year = new Date().getFullYear();
  const { links, email } = portfolio.identity;

  return (
    <main className="landing">
      <header className="landing__bar">
        <a href="#" className="landing__brand" onClick={(e) => e.preventDefault()}>
          <span className="landing__brand-logo">MK</span>
          <span className="md-title-m landing__brand-name">Mohan Krishna G R</span>
        </a>
        <button
          type="button"
          className="md-btn md-btn--icon"
          aria-label="Toggle theme"
          onClick={toggleTheme}
        >
          <span className="material-symbols-rounded">
            {theme === "dark" ? "light_mode" : "dark_mode"}
          </span>
        </button>
      </header>

      <section className="landing__hero">
        <div className="landing__hero-inner">
          <div>
            <span className="landing__eyebrow md-label-l">
              <span className="landing__eyebrow-dot" />
              <span className="landing__eyebrow-text landing__eyebrow-text-long">
                AI/ML Research Engineer · Available Fall 2026
              </span>
              <span className="landing__eyebrow-text landing__eyebrow-text-short">
                Available · Fall 2026
              </span>
            </span>

            <h1 className="landing__hello">
              Hi, I&rsquo;m <span className="accent">Mohan</span>.<br />
              I build systems that actually <br />
              scale and reason.
            </h1>

            <p className="landing__tagline">
              I&rsquo;m a final-year M.Tech CS student researching retrieval, reasoning,
              and the engineering it takes to ship them. <br /> Previously Generative AI research at{" "}
              <strong style={{ color: "var(--md-sys-color-on-surface)" }}>Airbus</strong>.
            </p>

            <div className="landing__actions">
              <a className="landing__cta" href="/portfolio" onClick={(e) => { e.preventDefault(); navigate("/portfolio"); }}>
                Enter the portfolio
                <span className="material-symbols-rounded">arrow_forward</span>
              </a>
              <a className="landing__resume" href={links.cv} target="_blank" rel="noreferrer">
                <span className="material-symbols-rounded">open_in_new</span>
                Résumé
              </a>
            </div>
          </div>

          <aside className="landing__socials">
            <div className="landing__socials-head md-label-l">Find me on</div>
            <div className="landing__socials-grid">
              <SocialCard
                href={links.linkedin}
                modifier="linkedin"
                label="LinkedIn"
                handle="in/grmk"
                icon={<LinkedInIcon />}
              />
              <SocialCard
                href={links.github}
                modifier="github"
                label="GitHub"
                handle="MohanKrishnaGR"
                icon={<GitHubIcon />}
              />
              <SocialCard
                href={links.x}
                modifier="x"
                label="X / Twitter"
                handle="@mohan_speaks_"
                icon={<XIcon />}
              />
              <SocialCard
                href={links.medium}
                modifier="medium"
                label="Medium"
                handle="@mohankrishnagr08"
                icon={<MediumIcon />}
              />
              <SocialCard
                href={links.substack}
                modifier="substack"
                label="Substack"
                handle="mohankrishnagr"
                icon={<SubstackIcon />}
                fullRow
              />
              <SocialCard
                href={`mailto:${email}`}
                modifier="gmail"
                label="Email · best reply"
                handle={email}
                icon={<GmailIcon />}
                fullRow
                external={false}
              />
            </div>
          </aside>
        </div>
      </section>

      <footer className="landing__footer md-body-s">
        <span className="landing__footer-key">
          <kbd>Enter</kbd> the portfolio
          <span style={{ opacity: 0.6 }}>·</span>
          <kbd>⌘</kbd> <kbd>K</kbd> to search
        </span>
        <span>© {year} Mohan Krishna G R · Coimbatore, India</span>
      </footer>
    </main>
  );
}

interface SocialCardProps {
  href: string;
  modifier: "linkedin" | "github" | "x" | "medium" | "substack" | "gmail";
  label: string;
  handle: string;
  icon: React.ReactNode;
  fullRow?: boolean;
  external?: boolean;
}

function SocialCard({ href, modifier, label, handle, icon, fullRow, external = true }: SocialCardProps) {
  return (
    <a
      href={href}
      className={`social-card social-card--${modifier}`}
      style={fullRow ? { gridColumn: "1 / -1" } : undefined}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      <span className="social-card__logo">{icon}</span>
      <div className="social-card__body">
        <div className="social-card__label">{label}</div>
        <div className="social-card__handle">{handle}</div>
      </div>
      <span className="material-symbols-rounded social-card__arrow" style={{ fontSize: 20 }}>
        arrow_outward
      </span>
    </a>
  );
}
